import {
  AfterViewInit,
  Component,
  ComponentRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { rowExpandAnimation } from "src/app/animations/row-expand-animation";
import LocalizationConstants from "src/app/constants/localization-constants";
import RouteNameConstants from "src/app/constants/route-name-constants";
import { Location } from "src/app/models/Location";
import { Tool } from "src/app/models/Tool";
import { CompanyService } from "src/app/services/company.service";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";
import { LocationService } from "src/app/services/location.service";
import { ModalService } from "src/app/services/modal.service";

import { ConfirmationDialogData } from "../common/confirmation-dialog/confirmation-dialog-modal.component";
import { LocationCreateModalComponent } from "./../location-create-modal/location-create-modal.component";
import { LocationEditModalComponent } from "./../location-edit-modal/location-edit-modal.component";
import { LocationMoveModalComponent } from "./../location-move-modal/location-move-modal.component";

export enum Actions {
  Default = "Default",
  Update = "Update",
  Remove = "Remove",
}

@Component({
  selector: "app-tool-location",
  templateUrl: "./tool-location.component.html",
  styleUrls: ["./tool-location.component.scss"],
  animations: [rowExpandAnimation],
  standalone: false,
})
export class ToolLocationComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  readonly routeNameConstants = RouteNameConstants;
  readonly localizationConstants = LocalizationConstants;

  dataSource = new MatTableDataSource<Location>([]);
  subscriptions: Subscription[] = [];
  isLoading: boolean;
  faSpinner = faSpinner;
  currentAction: string = Actions.Default;
  componentRef: ComponentRef<unknown>;

  constructor(
    public modalService: ModalService,
    public locationService: LocationService,
    public companyService: CompanyService,
    private globalOverlayService: GlobalOverlayService,
  ) {}

  ngOnInit() {
    this.subscriptions.push(this.onCompanyChange());

    if (!this.companyService.selectedCompany?.name) return;

    this.getLocations();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item) =>
      item.locationName + " - " + item.locationDescription;
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }

  getLocations() {
    this.globalOverlayService.show();
    this.locationService
      .getList(
        this.companyService.selectedCompany.name,
        this.companyService.selectedCompany.department.name,
      )
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe((response) => {
        if (!response) return;

        response.forEach((x) => (x.expanded = true));
        this.dataSource.data = response;
      });
  }

  removeFromLocation() {
    this.globalOverlayService.show();
    const location = <Location>{
      locationName: null,
      tools: this.selectedTools,
    };
    this.locationService
      .move(this.companyService.selectedCompany.name, location)
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe(() => this.getLocations());
  }

  async onActionsClick() {
    switch (this.currentAction) {
      case Actions.Update:
        this.openLocationMoveModal();
        break;
      case Actions.Remove:
        await this.verifyAndRemoveToolsFromLocation();
        break;
    }
    // after dropdown selection default option should be selected
    setTimeout(() => (this.currentAction = Actions.Default));
  }

  private async verifyAndRemoveToolsFromLocation(): Promise<void> {
    const resultObservable = this.modalService.openConfirmationModal(
      new ConfirmationDialogData(LocalizationConstants.LOCATIONS_REMOVE_TOOL_DIALOG_TITLE, [
        LocalizationConstants.LOCATIONS_REMOVE_TOOL_DIALOG_CONTENT,
      ]),
    );

    const canProceed = await resultObservable.toPromise();
    if (canProceed) {
      this.removeFromLocation();
    }
  }

  onCompanyChange() {
    return this.companyService.companyChange.subscribe((company) => {
      if (!company?.name) return;
      this.getLocations();
    });
  }

  openLocationCreateModal() {
    const resultObservable = this.modalService.openDefaultModal(
      LocationCreateModalComponent,
      this.dataSource.data,
    );

    const subscription = resultObservable.subscribe((result) => {
      if (result) {
        this.createLocation(result);
      }
    });

    this.subscriptions.push(subscription);
  }

  openLocationEditModal(location: Location) {
    const resultObservable = this.modalService.openDefaultModal(
      LocationEditModalComponent,
      location,
    );

    const subscription = resultObservable.subscribe((result) => {
      if (result) {
        this.editLocation(result);
      }
    });

    this.subscriptions.push(subscription);
  }

  openLocationDeleteModal(location: Location) {
    const resultObservable = this.modalService.openConfirmationModal(
      new ConfirmationDialogData(LocalizationConstants.LOCATION_DELETE_TITLE, [
        LocalizationConstants.LOCATION_DELETE_DESCRIPTION,
        LocalizationConstants.LOCATION_DELETE_CONFIRMATION,
      ]),
    );

    const subscription = resultObservable.subscribe((result) => {
      if (result) {
        this.deleteLocation(location);
      }
    });

    this.subscriptions.push(subscription);
  }

  openLocationMoveModal() {
    const resultObservable = this.modalService.openDefaultModal(LocationMoveModalComponent, {
      locations: this.dataSource.data,
      tools: this.selectedTools,
    });

    const subscription = resultObservable.subscribe((result) => {
      if (result) {
        this.moveLocation(result);
      }
    });

    this.subscriptions.push(subscription);
  }

  private deleteLocation(location: Location) {
    this.globalOverlayService.show();
    this.locationService
      .delete(this.companyService.selectedCompany.name, location)
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe(() => this.getLocations());
  }

  private createLocation(location: Location) {
    this.globalOverlayService.show();
    this.locationService
      .create(this.companyService.selectedCompany.name, location)
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe(() => this.getLocations());
  }

  private editLocation(location: Location) {
    this.globalOverlayService.show();
    this.locationService
      .update(this.companyService.selectedCompany.name, location)
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe(() => this.getLocations());
  }

  private moveLocation(location: Location) {
    this.globalOverlayService.show();
    this.locationService
      .move(this.companyService.selectedCompany.name, location)
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe(() => this.getLocations());
  }

  get selectedTools() {
    const selectedTools: Tool[] = [];
    this.dataSource.data.forEach((x) =>
      x.tools.filter((t) => t.selected).forEach((t) => selectedTools.push(t)),
    );
    return selectedTools;
  }

  public getToolsDataSource(tools: Tool[], matSort: MatSort): MatTableDataSource<Tool> {
    const dataSource = new MatTableDataSource<Tool>(tools);
    dataSource.sort = matSort;
    return dataSource;
  }

  public toggleExpandRow(row: Location): void {
    row.expanded = !row.expanded;
  }

  public isExpansionDetailRow(index: number, location: Location): boolean {
    return location.tools.length > 0;
  }

  // TODO: Check business requirements - can be used for toggling all checkboxes in a location

  // public isAllSelectedInLocation(location: Location): boolean {
  //   return !location.tools.some((tool) => !tool.selected);
  // }

  // public isIndeterminateSelectionInLocation(location: Location): boolean {
  //   const selectedTools = location.tools.filter((tool) => tool.selected);
  //   return (
  //     selectedTools.length > 0 && selectedTools.length < location.tools.length
  //   );
  // }

  // public toggleSelectAllInLocation(location): void {
  //   const selected = !this.isAllSelectedInLocation(location);
  //   location.tools.forEach((tool) => (tool.selected = selected));
  // }
}
